/**
 * Display formatters shared by widget components. Driven by the
 * `format` field on a KPI registry entry (currency_usd, percent, etc.).
 *
 * Percent convention (MER-176): values reaching this formatter are
 * already in percent units (0-100), not fractions (0-1). The canonical
 * Postgres GENERATED columns (migrations/0008) and the Python
 * `normalize.py` aggregates both multiply by 100 at compute time, so
 * the formatter only appends the `%` glyph. Any source that produces
 * a fraction (e.g. `is_completed.avg()`) must scale to percent units
 * before calling `formatValue` — see `queries.ts` completion_pct path.
 */

/**
 * Compact axis/tick formatting ("$1.00M", "403k") lives in
 * `lib/compactNumber.mjs`, not here: it is a chart-axis concern, and a
 * KPI tile deliberately shows the exact dollars ("$1,002,966") because a
 * tile has the width for them and a finance reader wants them.
 */

import { isMoneyColumnName } from "./moneyColumns.mjs";

export type KpiFormat =
  | "currency_usd"
  | "percent"
  | "integer"
  | "decimal_1"
  | "decimal_2"
  | "ratio"
  | "days"
  | "text";

/**
 * Map a manifest `display_format` string (which may use slightly
 * different aliases like "currency" or "percentage") to a canonical
 * KpiFormat. Centralized so callers outside WidgetRenderer (Chart,
 * ChartWidget, KpiComparisonTile) stay in sync.
 *
 * Default falls back to decimal_2 for unknown values so a typo in
 * the manifest renders a number without erroring.
 */
export function asKpiFormat(value: string | undefined): KpiFormat {
  switch (value) {
    case "currency_usd":
    case "currency":
      return "currency_usd";
    case "percent":
    case "percentage":
      return "percent";
    case "integer":
    case "number":
      return "integer";
    case "decimal_1":
      return "decimal_1";
    case "decimal_2":
    case "decimal":
      return "decimal_2";
    case "ratio":
      return "ratio";
    // A day count (DSO, customer pay time). Before this case "days" fell to
    // the decimal_2 default and the Board's DSO tile read "32.90" with no unit.
    case "days":
      return "days";
    case "text":
    case "string":
      return "text";
    default:
      return "decimal_2";
  }
}

/**
 * Heuristic KpiFormat from a column name + label (MER-180).
 *
 * Per-cell renderers like the measure pivot table need a different format
 * per column. The manifest's widget-level `display_format` is the wrong
 * granularity for tables that mix Revenue ($), Cost ($), and Margin %
 * columns. This helper inspects the column / label naming convention to
 * pick a sensible format when the measure itself doesn't declare one.
 *
 * Returns `null` if no strong signal so the caller can fall back to the
 * widget-level format. Conservative on purpose so existing widgets
 * don't change behavior just because they happened to match a weak rule.
 */
export function inferFormatFromName(
  column: string | undefined,
  label: string | undefined,
): KpiFormat | null {
  const col = (column ?? "").toLowerCase();
  const lab = (label ?? "").toLowerCase();
  if (
    lab.includes("%") ||
    /(^|_)pct($|_)/.test(col) ||
    lab.endsWith(" pct") ||
    /rate_pct$/.test(col)
  ) {
    return "percent";
  }
  if (
    isMoneyColumnName(col) ||
    /\b(revenue|cost|margin|cash|balance|p&l|fees?|earned|billed|disposal|cogs|ebitda|ar|emr)\b/.test(
      lab,
    )
  ) {
    return "currency_usd";
  }
  if (/_count$|_qty$|_total_count$|stops$|hauls$|lifts$|swaps$|visits$|orders$|events$/.test(col)) {
    return "integer";
  }
  return null;
}

export function formatValue(value: number | string | null | undefined, fmt: KpiFormat): string {
  if (value === null || value === undefined) return "n/a";
  // MER-306 close: KPI tiles can surface text categoricals (e.g. route_kpis
  // .route_margin_trend_direction = 'improving' / 'stable' / 'worsening').
  // The numeric formatters below would silently coerce a string to NaN
  // and the tile would render n/a, so pass strings straight through
  // when the widget declares display_format: text. Numeric formats
  // still reject non-numeric strings as n/a so a manifest mismatch
  // surfaces visibly rather than rendering "NaN".
  if (fmt === "text") {
    return typeof value === "string" ? value : String(value);
  }
  if (typeof value === "string") return "n/a";
  if (Number.isNaN(value)) return "n/a";
  switch (fmt) {
    case "currency_usd": {
      // MER-578: cents matter below $100 and are noise above it. A flat
      // maximumFractionDigits: 0 rendered Avg Cost / Ton as "$21" when the
      // figure is $20.69 -- on a per-unit rate that a hauler negotiates, the
      // cents ARE the number. The same tile shape carries $224,157, where two
      // decimals would be clutter, so the precision follows the magnitude
      // rather than the column.
      const cents = Math.abs(value) > 0 && Math.abs(value) < 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: cents ? 2 : 0,
        maximumFractionDigits: cents ? 2 : 0,
      }).format(value);
    }
    case "percent":
      // MER-184: canonical _pct columns are now fractions (0-1). Multiply by
      // 100 for display so a stored 0.42 renders as "42.0%". Reverses MER-176.
      return `${(value * 100).toFixed(1)}%`;
    case "integer":
      return new Intl.NumberFormat("en-US").format(Math.round(value));
    // MER-578: group thousands. toFixed never groups, so a decimal tile read
    // "15770.4" while the integer tile beside it read "15,770" -- inconsistent
    // within one row, and Total Tons sits on the landing page.
    case "decimal_1":
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value);
    case "decimal_2":
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    case "ratio":
      return `${value.toFixed(2)}x`;
    // A day count: one decimal and the unit, so DSO reads "32.9 days", not a
    // bare "32.90" a reader has to guess the unit of.
    case "days":
      return `${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value)} days`;
  }
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format an ISO date string for a table cell, parsed lexically to avoid
 * timezone drift (MER-438). "2026-06-01" / "2026-06-01T12:00:00Z" render as
 * "Jun 1"; "2026-06" renders as "Jun 2026". Returns null when the value is not
 * an ISO date so the caller can fall back to its default rendering.
 */
export function formatTableDate(value: string): string | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (day) {
    const m = Number(day[2]);
    if (m >= 1 && m <= 12) return `${MONTHS[m - 1]} ${Number(day[3])}`;
    return null;
  }
  const month = /^(\d{4})-(\d{2})$/.exec(value);
  if (month) {
    const m = Number(month[2]);
    if (m >= 1 && m <= 12) return `${MONTHS[m - 1]} ${month[1]}`;
    return null;
  }
  return null;
}

/**
 * Label a weekly period's start date so it reads as a span, not a single day
 * (MER-438 addendum): a weekly row's "2026-05-04" rendered as bare "May 4".
 * Returns "Week of May 4"; null for non-ISO input.
 */
export function formatWeekStart(value: string): string | null {
  const d = formatTableDate(value);
  return d ? `Week of ${d}` : null;
}
